import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { useCapabilityDetail } from '@/hooks/use-entity-detail';
import { useCapabilityUpdate } from '@/hooks/use-entity-mutations';
import { Globe, Loader2, Lock, Pencil, Puzzle, Shield, Wrench, X, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityActions } from '@/hooks/use-capability-actions';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { CapabilityHeaderActions } from '@/components/capability-detail/CapabilityHeaderActions';
import { IntegrationOverviewTab } from './components/integration-overview-tab';
import { IntegrationAuthTab } from './components/integration-auth-tab';
import { IntegrationMethodsTab } from './components/integration-methods-tab';
import { ToolCapabilityPickerDrawer, type ToolCapabilityOption } from './components/ToolCapabilityPickerDrawer';
import { cn } from '@/lib/utils';
import { capabilityDisplayTitle, CAPABILITY_TYPE_LABELS } from '@/types';
import { capabilityColors, getCapabilityColors, getStatusStyle } from '@/lib/capability-colors';
import { usePageTitle } from '@/hooks/use-page-title';

const LOG_PREFIX = 'integration-detail-page';

interface IntegrationTypeConfig {
    protocol?: string;
    base_url?: string;
    toolRef?: string;
    app?: {
        name?: string;
        vendor?: string;
        category?: string;
        description?: string;
        icon_url?: string;
        documentation_url?: string;
    };
    mcp?: {
        transport?: string;
        server_url?: string;
        discovery?: boolean;
    };
    auth?: {
        supported_types?: string[];
        strategies?: Record<string, unknown>;
    };
    common_headers?: Record<string, string>;
    token_injection?: {
        location?: string;
        key?: string;
        template?: string;
    };
    method_map?: Record<string, unknown>;
}

function extractTypeConfig(data: Record<string, unknown> | undefined): IntegrationTypeConfig {
    if (!data) return {};
    const wr = data.working_revision as { type_config?: Record<string, unknown> } | undefined;
    const raw = (wr?.type_config ?? data.config ?? {}) as Record<string, unknown>;

    // Registry import nests the manifest's type_config inside the revision's
    // type_config column, producing { type_config: { base_url, ... }, ... }.
    // Unwrap so the UI sees base_url, protocol, etc. at the top level.
    // Top-level (rest) keys take priority over nested ones because UI saves
    // write flat keys — the nested copy may be stale.
    if (raw.type_config && typeof raw.type_config === 'object' && !Array.isArray(raw.type_config)) {
        const nested = raw.type_config as Record<string, unknown>;
        const { type_config: _, ...rest } = raw;
        return { ...nested, ...rest } as IntegrationTypeConfig;
    }

    return raw as IntegrationTypeConfig;
}

export default function IntegrationDetailPage() {
    const { appKey, name } = useParams<{ appKey: string; name: string }>();
    const { identity, currentOrganization } = useAuth();
    const { data, isLoading, refetch } = useCapabilityDetail(appKey!, name!);
    const [activeTab, setActiveTab] = useState('overview');
    const [overviewPatch, setOverviewPatch] = useState<Record<string, unknown>>({});
    const [authPatch, setAuthPatch] = useState<Record<string, unknown>>({});
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
    const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

    const updateMutation = useCapabilityUpdate(appKey!, name!);

    const tc = useMemo(() => extractTypeConfig(data as Record<string, unknown> | undefined), [data]);

    const [toolRef, setToolRef] = useState('');
    const [tools, setTools] = useState<ToolCapabilityOption[]>([]);
    const [loadingTools, setLoadingTools] = useState(false);
    const [isToolDrawerOpen, setIsToolDrawerOpen] = useState(false);

    useEffect(() => {
        setLoadingTools(true);
        api.get('/capabilities', { params: { type: 'tool', per_page: 1000, include_entitled: 'true' } })
            .then((res) => {
                const payload = res.data;
                const items = (
                    Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
                ) as ToolCapabilityOption[];
                setTools(
                    items.map((t) => ({
                        name: t.name,
                        description: t.description,
                        tags: Array.isArray(t.tags) ? t.tags : [],
                        app_key: t.app_key,
                        visibility: t.visibility,
                        created_by: t.created_by,
                        organization_id: t.organization_id,
                        access_source: t.access_source,
                    }))
                );
            })
            .catch(() => setTools([]))
            .finally(() => setLoadingTools(false));
    }, []);

    useEffect(() => {
        const configToolRef = tc.toolRef ?? '';
        setToolRef(configToolRef);
    }, [tc.toolRef]);

    const extractedToolRef = tc.toolRef ?? '';
    const toolRefDirty = toolRef !== extractedToolRef;

    const methodCount = useMemo(() => {
        const mm = tc.method_map;
        return mm ? Object.keys(mm).length : 0;
    }, [tc.method_map]);
    const tags = useMemo(() => {
        const rawTags = (data as { tags?: unknown } | undefined)?.tags;
        if (!Array.isArray(rawTags)) return [];
        return rawTags.filter((tag): tag is string => typeof tag === 'string');
    }, [data]);
    const capabilityDisplayName = ((data as { display_name?: string } | undefined)?.display_name ?? '') as string;
    const capabilityDescription = ((data as { description?: string } | undefined)?.description ?? '') as string;

    const handleSave = useCallback(async (updates: Record<string, unknown>) => {
        try {
            await updateMutation.mutateAsync(updates);
            toast.success('Integration updated');
            return true;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to update';
            toast.error(msg);
            return false;
        }
    }, [updateMutation]);
    const handleOverviewDeferredDirtyChange = useCallback((payload: { dirty: boolean; patch: Record<string, unknown> }) => {
        setOverviewPatch((prev) => {
            const next = payload.dirty ? payload.patch : {};
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
        });
    }, []);
    const handleAuthDeferredDirtyChange = useCallback((payload: { dirty: boolean; patch: Record<string, unknown> }) => {
        setAuthPatch((prev) => {
            const next = payload.dirty ? payload.patch : {};
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
        });
    }, []);
    const overviewIsDirty = Object.keys(overviewPatch).length > 0;
    const authIsDirty = Object.keys(authPatch).length > 0;
    const anyDirty = overviewIsDirty || authIsDirty || toolRefDirty;

    const handleSaveAll = useCallback(async () => {
        if (!anyDirty) return;
        const merged: Record<string, unknown> = {};
        if (overviewIsDirty) Object.assign(merged, overviewPatch);
        if (authIsDirty) {
            const existingTc = (merged.type_config ?? {}) as Record<string, unknown>;
            const authTc = (authPatch.type_config ?? {}) as Record<string, unknown>;
            merged.type_config = { ...existingTc, ...authTc };
        }
        if (toolRefDirty) {
            const existingTc = (merged.type_config ?? {}) as Record<string, unknown>;
            merged.type_config = { ...existingTc, toolRef: toolRef || null };
        }
        const ok = await handleSave(merged);
        if (ok) {
            setOverviewPatch({});
            setAuthPatch({});
        }
    }, [anyDirty, overviewIsDirty, authIsDirty, toolRefDirty, toolRef, overviewPatch, authPatch, handleSave]);

    const overviewFormDisabled = updateMutation.isPending;

    const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
    const HEADER_LAYER_ID = 'integration-page';

    const actions = useCapabilityActions({
        appKey: appKey!,
        name: name!,
        typeLabel: 'Integration',
        data: data as Record<string, unknown> | null,
        refetch,
        customSave: handleSaveAll,
        customIsDirty: anyDirty,
        customIsSaving: updateMutation.isPending,
    });

    const saveRef = useRef(actions.save);
    saveRef.current = actions.save;
    const publishRef = useRef(actions.publish);
    publishRef.current = actions.publish;
    const editRef = useRef(actions.edit);
    editRef.current = actions.edit;

    const d = data as Record<string, unknown> | undefined;
    const hasWorkingRevision = (d as { working_revision?: unknown } | undefined)?.working_revision != null;
    const isArchivedFlag = ((d?.status as string) ?? '').toLowerCase() === 'archived';

    useEffect(() => {
        if (isLoading || !data) {
            removeHeaderLayer(HEADER_LAYER_ID);
            return;
        }
        const capType = (d?.type as string) ?? 'integration';
        const colors = getCapabilityColors(capType);
        const Icon = colors.icon ?? Puzzle;
        const fqcn = (d?.fqcn as string) || `${appKey}/${name}`;
        const statusStyle = getStatusStyle((d?.status as string) ?? 'active');
        const headerTitle = d
            ? capabilityDisplayTitle({
                name: (d.name as string) ?? name ?? 'integration',
                display_name: d.display_name as string | null | undefined,
            })
            : name ?? '';
        const badges = (
            <div className="flex items-center gap-1.5 shrink-0">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bgLight} ${colors.text}`}>
                    {CAPABILITY_TYPE_LABELS[capType] || capType}
                </span>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyle}`}>
                    {(d?.status as string) ?? 'active'}
                </span>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
                    {(d?.visibility as string) || 'account'}
                </span>
                {isArchivedFlag && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                        <Lock size={11} /> Archived
                    </span>
                )}
                {tc.app?.vendor && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">
                        by {tc.app.vendor}
                    </span>
                )}
                {tc.protocol && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 uppercase">
                        <Globe size={11} />
                        {tc.protocol}
                    </span>
                )}
                {methodCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        <Zap size={11} />
                        {methodCount} method{methodCount !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
        );
        const titleBlock = {
            icon: (
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${colors.bgLight}`}>
                    <Icon size={16} className={colors.text} />
                </div>
            ),
            title: headerTitle,
            subtitle: fqcn,
            badges,
        };
        if (isArchivedFlag) {
            pushHeaderLayer(HEADER_LAYER_ID, { title: titleBlock });
            return () => removeHeaderLayer(HEADER_LAYER_ID);
        }
        pushHeaderLayer(HEADER_LAYER_ID, {
            title: titleBlock,
            action: (
                <CapabilityHeaderActions
                    typeLabel="Integration"
                    hasWorkingRevision={actions.hasWorkingRevision}
                    isViewingPublished={actions.isViewingPublished}
                    isArchived={isArchivedFlag}
                    selectedPublishedLabel={actions.selectedPublishedLabel}
                    isDirty={actions.isDirty}
                    isSaving={actions.isSaving}
                    isPublishing={actions.isPublishing}
                    onSave={() => void saveRef.current()}
                    onPublish={() => void publishRef.current()}
                    onEdit={() => editRef.current()}
                    onArchive={() => actions.openArchiveDialog()}
                    onSettings={() => actions.openSettings()}
                />
            ),
        });
        return () => removeHeaderLayer(HEADER_LAYER_ID);
    }, [
        isLoading,
        data,
        appKey,
        name,
        isArchivedFlag,
        actions.hasWorkingRevision,
        actions.isViewingPublished,
        actions.selectedPublishedLabel,
        actions.isDirty,
        actions.isSaving,
        actions.isPublishing,
        pushHeaderLayer,
        removeHeaderLayer,
    ]);

    const tabButtonClass = (tab: string) =>
        cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            activeTab === tab
                ? 'bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm'
                : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]',
        );

    const title = d
        ? capabilityDisplayTitle({
            name: (d.name as string) ?? name ?? 'integration',
            display_name: d.display_name as string | null | undefined,
        })
        : name ?? '';
    usePageTitle(`Integration - ${title}`);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!data) {
        return <div className="p-6">Not found</div>;
    }

    const formReadOnly = isArchivedFlag || !hasWorkingRevision || isViewingPublished;

    const toolColors = capabilityColors.tool;
    const selectedTool = tools.find(
        (t) => t.name === toolRef || (t.app_key && `${t.app_key}/${t.name}` === toolRef)
    );
    const toolDisplayName = selectedTool?.name ?? (toolRef.includes('/') ? toolRef.split('/').pop() : toolRef);

    const toolCapabilitySlot = (
        <div
            id="integration-tool-capability"
            role="button"
            tabIndex={formReadOnly ? -1 : 0}
            onClick={() => {
                if (!formReadOnly) setIsToolDrawerOpen(true);
            }}
            onKeyDown={(e) => {
                if (formReadOnly) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsToolDrawerOpen(true);
                }
            }}
            className={cn(!formReadOnly && 'cursor-pointer')}
        >
            {toolRef ? (
                <Card
                    className={cn(
                        'relative border-2 p-3 transition-all duration-200 min-h-[88px]',
                        'border-l-4 border-slate-200 bg-white hover:shadow-md'
                    )}
                    style={{ borderLeftColor: toolColors.hex }}
                >
                    <div className="flex flex-row items-start gap-3">
                        <div
                            className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                toolColors.bgLight,
                                toolColors.text
                            )}
                        >
                            <Wrench size={16} />
                        </div>
                        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                tool capability
                            </span>
                            <span className="text-sm font-bold truncate text-slate-900">
                                {toolDisplayName}
                            </span>
                            {selectedTool?.description && (
                                <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                                    {selectedTool.description}
                                </span>
                            )}
                        </div>
                        {!formReadOnly && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setToolRef('');
                                }}
                                aria-label="Remove tool capability"
                            >
                                <X size={10} />
                            </Button>
                        )}
                    </div>
                </Card>
            ) : (
                <Card
                    className={cn(
                        'border-2 border-dashed border-slate-200 bg-slate-50/50 p-3',
                        formReadOnly && 'pointer-events-none opacity-60'
                    )}
                >
                    <div className="flex flex-col items-center justify-center py-2">
                        <span className="mb-1 text-violet-400">
                            <Wrench size={24} />
                        </span>
                        <p className="text-xs font-medium text-violet-600">
                            {formReadOnly ? 'No tool capability' : 'Click to select tool capability'}
                        </p>
                        {loadingTools && !formReadOnly && (
                            <p className="text-xs text-slate-400 mt-1">Loading tool capabilities...</p>
                        )}
                        <p className="text-xs text-slate-500 mt-2 italic">
                            The tool capability that defines the functions exposed by this integration.
                        </p>
                    </div>
                </Card>
            )}
        </div>
    );

    return (
        <>
            <CapabilityDetailLayout
                typeLabel="Integration"
                appKey={appKey}
                capabilityName={name}
                entityData={data as Record<string, unknown> | undefined}
                selectedRevisionId={selectedRevisionId}
                onRevisionChange={setSelectedRevisionId}
                onOverviewDeferredDirtyChange={handleOverviewDeferredDirtyChange}
                overviewFormDisabled={overviewFormDisabled || !hasWorkingRevision || isViewingPublished}
            >
                <div className="flex flex-col pt-6">
                    <div className="inline-flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
                        <button type="button" className={tabButtonClass('overview')} onClick={() => setActiveTab('overview')}>
                            <Globe size={14} />
                            Overview
                        </button>
                        <button type="button" className={tabButtonClass('authorization')} onClick={() => setActiveTab('authorization')}>
                            <Shield size={14} />
                            Authorization
                        </button>
                        <button type="button" className={tabButtonClass('methods')} onClick={() => setActiveTab('methods')}>
                            <Zap size={14} />
                            Methods
                            {methodCount > 0 && (
                                <span className="ml-1 rounded-full bg-white/70 px-1.5 py-0.5 text-[10px] text-slate-600">
                                    {methodCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {activeTab === 'overview' && (
                        <div className="mt-2">
                            <IntegrationOverviewTab
                                typeConfig={tc}
                                tags={tags}
                                displayName={capabilityDisplayName}
                                description={capabilityDescription}
                                onDeferredDirtyChange={handleOverviewDeferredDirtyChange}
                                onPersist={async (patch) => {
                                    const ok = await handleSave(patch);
                                    if (ok) setOverviewPatch({});
                                    return ok;
                                }}
                                disabled={overviewFormDisabled || formReadOnly}
                                toolCapabilitySlot={toolCapabilitySlot}
                            />
                        </div>
                    )}

                    {activeTab === 'authorization' && (
                        <div className="mt-2">
                            <IntegrationAuthTab
                                typeConfig={tc as Record<string, unknown>}
                                onSave={handleSave}
                                isSaving={updateMutation.isPending}
                                onDeferredDirtyChange={handleAuthDeferredDirtyChange}
                                disabled={overviewFormDisabled || formReadOnly}
                            />
                        </div>
                    )}

                    {activeTab === 'methods' && (
                        <div className="mt-2">
                            <IntegrationMethodsTab
                                typeConfig={tc as Record<string, unknown>}
                                canonicalMethods={
                                    ((d?.working_revision as Record<string, unknown> | undefined)?.methods ??
                                        d?.methods) as Record<string, unknown>[] | undefined
                                }
                                onSave={handleSave}
                                isSaving={updateMutation.isPending}
                                appKey={appKey}
                                currentUserId={identity?.sub ?? ''}
                                currentOrgId={currentOrganization?.id ?? ''}
                            />
                        </div>
                    )}
                </div>
            </CapabilityDetailLayout>

            <ToolCapabilityPickerDrawer
                isOpen={isToolDrawerOpen && !formReadOnly}
                onClose={() => setIsToolDrawerOpen(false)}
                tools={tools}
                selectedToolRef={toolRef}
                onSelect={(toolName) => setToolRef(toolName)}
                loading={loadingTools}
                currentUserId={identity?.sub ?? ''}
                currentOrgId={currentOrganization?.id ?? ''}
            />

            <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Pencil size={18} className="text-amber-600" />
                            Replace working copy?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            A working copy already exists. Replacing it will discard any unsaved changes
                            and create a new working copy based on the selected published version.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={actions.handleConfirmReplace}>
                            Replace
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
